/**
 * Cart Dispatch Calculator
 * 
 * Calculates estimated dispatch dates for cart based on product metafields,
 * stock levels, release dates, and working day configuration.
 * 
 * Key Features:
 * - Automatically syncs dispatch delay line item properties for out-of-stock items
 * - Respects priority: Release dates > Stock-based delays > Default timing
 * - Applies delays only when quantity exceeds available stock
 * - Debounced updates for optimal performance (prevents excessive API calls)
 * - Batched property updates via single /cart/update.js call
 * - Non-blocking async operations to maintain smooth UX
 * 
 * Line Item Properties Applied:
 * - `_Release Date`: Applied when product has future release date (via Liquid)
 * - `_Dispatch Delay`: Applied when quantity exceeds stock (via JavaScript, can be disabled)
 * 
 * Properties prefixed with underscore (_) are hidden from customers but visible
 * in order details for fulfillment teams.
 * 
 * Performance Optimizations:
 * - Debounced recalculation (150ms default)
 * - Debounced property sync (300ms default for batch updates)
 * - Early exit conditions to avoid unnecessary processing
 * - Efficient cart state comparison to minimize re-renders
 * - Async/await for non-blocking updates
 * - Single batch update per sync cycle
 * 
 * @example
 * // Manual refresh (if needed)
 * window.cartDispatchCalculator.refresh();
 * 
 * @example
 * // Enable debug mode in theme settings
 * // Theme Settings > Cart Dispatch Calculator > Debug Mode
 * 
 * @example
 * // Disable dispatch delay line item properties (keeps release date properties)
 * window.cartDispatchCalculator.enableDispatchDelayProperties = false;
 */

class CartDispatchCalculator {
  constructor() {
    this.workingDays = this.getWorkingDaysFromSettings();
    this.holidays = this.getHolidaysFromSettings();
    this.debug = window.themeSettings?.dispatch_debug_mode || false;
    
    // Feature flag: Enable/disable dispatch delay line item properties
    // Set to false to disable dispatch delay properties (release date properties remain active)
    this.enableDispatchDelayProperties = window.themeSettings?.dispatch_enable_delay_properties ?? false;
    
    // Debounce timers for performance optimization
    this.recalculateTimeout = null;
    this.syncPropertiesTimeout = null;
    this.isProcessing = false;
    
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.recalculateDispatch();
    this.setupEventListeners();
    
    if (this.debug) {
      console.log('Cart Dispatch Calculator initialized');
    }
  }

  /**
   * Get working days configuration from theme settings
   * @returns {number[]} Array of working day numbers (0=Sunday, 6=Saturday)
   */
  getWorkingDaysFromSettings() {
    const days = [];
    if (window.themeSettings?.dispatch_monday) days.push(1);
    if (window.themeSettings?.dispatch_tuesday) days.push(2);
    if (window.themeSettings?.dispatch_wednesday) days.push(3);
    if (window.themeSettings?.dispatch_thursday) days.push(4);
    if (window.themeSettings?.dispatch_friday) days.push(5);
    if (window.themeSettings?.dispatch_saturday) days.push(6);
    if (window.themeSettings?.dispatch_sunday) days.push(0);
    
    // Default to Mon-Fri if no settings
    return days.length > 0 ? days : [1, 2, 3, 4, 5];
  }

  /**
   * Get holidays configuration from theme settings
   * @returns {string[]} Array of holiday dates in dd/mm/yyyy format
   */
  getHolidaysFromSettings() {
    const holidaysString = window.themeSettings?.dispatch_holidays || '';
    return holidaysString.split('\n')
      .map(date => date.trim())
      .filter(date => date.length > 0);
  }

  /**
   * Get the effective start date for dispatch calculations, accounting for cutoff time
   * @returns {Date} The effective start date (today or next working day if after cutoff)
   */
  getEffectiveStartDate() {
    const now = new Date();
    const cutoffTime = window.themeSettings?.dispatch_cutoff_time;
    
    // If no cutoff time is set, use current date
    if (!cutoffTime || cutoffTime.trim() === '') {
      return now;
    }
    
    try {
      // Parse cutoff time (expected format: "HH:MM")
      const [hours, minutes] = cutoffTime.split(':').map(num => parseInt(num, 10));
      
      if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        if (this.debug) {
          console.warn('Invalid cutoff time format:', cutoffTime, '- using current date');
        }
        return now;
      }
      
      // Create cutoff time for today
      const todayCutoff = new Date(now);
      todayCutoff.setHours(hours, minutes, 0, 0);
      
      // If current time is after cutoff, start from next working day
      if (now > todayCutoff) {
        const nextWorkingDay = this.getNextWorkingDay(now);
        
        if (this.debug) {
          console.log('After cutoff time:', {
            currentTime: now.toLocaleTimeString(),
            cutoffTime: cutoffTime,
            nextWorkingDay: this.formatDate(nextWorkingDay)
          });
        }
        
        return nextWorkingDay;
      }
      
      if (this.debug) {
        console.log('Before cutoff time:', {
          currentTime: now.toLocaleTimeString(),
          cutoffTime: cutoffTime,
          usingDate: this.formatDate(now)
        });
      }
      
      return now;
      
    } catch (error) {
      if (this.debug) {
        console.warn('Error parsing cutoff time:', cutoffTime, error);
      }
      return now;
    }
  }

  /**
   * Get the next working day from a given date
   * @param {Date} fromDate - Starting date
   * @returns {Date} Next working day
   */
  getNextWorkingDay(fromDate) {
    const result = new Date(fromDate);
    
    do {
      result.setDate(result.getDate() + 1);
      const dayOfWeek = result.getDay();
      const dateString = this.formatDate(result);
      
      // Check if it's a working day and not a holiday
      if (this.workingDays.includes(dayOfWeek) && !this.holidays.includes(dateString)) {
        return result;
      }
    } while (true);
  }

  /**
   * Add working days to a date, skipping weekends and holidays
   * @param {Date} startDate - Starting date
   * @param {number} days - Number of working days to add
   * @param {number[]} workingDays - Array of working day numbers
   * @param {string[]} holidays - Array of holiday dates in dd/mm/yyyy format
   * @returns {Date} New date with working days added
   */
  addWorkingDays(startDate, days, workingDays = this.workingDays, holidays = this.holidays) {
    if (days === 0) return new Date(startDate);
    
    const result = new Date(startDate);
    let addedDays = 0;
    
    while (addedDays < days) {
      result.setDate(result.getDate() + 1);
      
      const dayOfWeek = result.getDay();
      const dateString = this.formatDate(result);
      
      // Check if it's a working day and not a holiday
      if (workingDays.includes(dayOfWeek) && !holidays.includes(dateString)) {
        addedDays++;
      }
    }
    
    return result;
  }

  /**
   * Format date as dd/mm/yyyy
   * @param {Date} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDate(date) {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  /**
   * Parse date from dd/mm/yyyy format
   * @param {string} dateString - Date string in dd/mm/yyyy format
   * @returns {Date} Parsed date
   */
  parseDate(dateString) {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Parse release date from ISO format (YYYY-MM-DD) or dd/mm/yyyy format
   * @param {string} dateString - Date string in ISO format or dd/mm/yyyy format
   * @returns {Date} Parsed date
   */
  parseReleaseDate(dateString) {
    if (!dateString) return null;
    
    // Handle ISO format (YYYY-MM-DD)
    if (dateString.includes('-')) {
      const date = new Date(dateString);
      // Validate the date
      if (isNaN(date.getTime())) {
        console.warn('Invalid ISO date format:', dateString);
        return null;
      }
      return date;
    }
    
    // Handle dd/mm/yyyy format (fallback)
    if (dateString.includes('/')) {
      try {
        return this.parseDate(dateString);
      } catch (e) {
        console.warn('Invalid dd/mm/yyyy date format:', dateString);
        return null;
      }
    }
    
    console.warn('Unrecognized date format:', dateString);
    return null;
  }

  /**
   * Parse all dispatch data from JSON script tags
   * @returns {object[]} Array of dispatch data objects
   */
  parseDispatchData() {
    // Parse regular cart dispatch data
    const cartScripts = document.querySelectorAll('script.dispatch-data[type="application/json"]');
    const data = [];
    
    cartScripts.forEach(script => {
      try {
        const itemData = JSON.parse(script.textContent);
        if (itemData && itemData.product_id) {
          data.push(itemData);
        }
      } catch (e) {
        console.warn('Invalid dispatch data JSON:', script, e);
      }
    });
    
    if (this.debug) {
      console.log('Parsed dispatch data:', data);
    }
    
    return data;
  }

  /**
   * Fetch current cart data
   * @returns {Promise<object>} Cart object
   */
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [] };
    }
  }

  /**
   * Determine if a cart item needs dispatch delay property
   * @param {object} item - Cart line item
   * @param {object} dispatchData - Dispatch data for this item
   * @returns {object|null} Delay info or null if no delay needed
   */
  calculateItemDispatchDelay(item, dispatchData) {
    const quantity = parseInt(item.quantity) || 0;
    const inStock = parseInt(dispatchData.in_stock_quantity) || 0;
    const releaseDate = dispatchData.release_date;
    const minDelay = parseInt(dispatchData.dispatch_delay_min) || 0;
    const maxDelay = parseInt(dispatchData.dispatch_delay_max) || 0;

    // Priority 1: Check if quantity exceeds stock first
    const needsSupplierStock = quantity > inStock;
    
    // Priority 2: Release date only applies if stock is insufficient
    // We check if it's in the future and stock is needed
    if (releaseDate && needsSupplierStock) {
      const releaseDateObj = this.parseReleaseDate(releaseDate);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      
      if (releaseDateObj && releaseDateObj >= now) {
        // Release date property should already exist, no delay property needed
        return null;
      }
    }

    // Priority 3: Stock availability determines if delay is needed
    
    if (!needsSupplierStock) {
      // In stock - no delay property needed
      return null;
    }

    // Out of stock and continuing - apply delay
    const effectiveMinDelay = minDelay || window.themeSettings?.dispatch_default_min_days || 1;
    const effectiveMaxDelay = maxDelay || window.themeSettings?.dispatch_default_max_days || 3;

    // Calculate dispatch dates
    const effectiveStartDate = this.getEffectiveStartDate();
    const minDate = this.addWorkingDays(effectiveStartDate, effectiveMinDelay);
    const maxDate = this.addWorkingDays(effectiveStartDate, effectiveMaxDelay);

    return {
      minDelay: effectiveMinDelay,
      maxDelay: effectiveMaxDelay,
      minDate: this.formatDate(minDate),
      maxDate: this.formatDate(maxDate),
      message: this.formatDispatchMessage(effectiveMinDelay, effectiveMaxDelay)
    };
  }

  /**
   * Format dispatch delay message
   * @param {number} minDays
   * @param {number} maxDays
   * @returns {string}
   */
  formatDispatchMessage(minDays, maxDays) {
    if (minDays === maxDays) {
      return minDays === 1 
        ? `${minDays} day`
        : `${minDays} days`;
    }
    return `${minDays}-${maxDays} days`;
  }

  /**
   * Sync line item properties for all cart items
   * Updates dispatch delay properties based on current stock levels
   */
  async syncLineItemProperties() {
    // Skip if dispatch delay properties are disabled
    if (!this.enableDispatchDelayProperties) {
      if (this.debug) {
        console.log('Dispatch delay properties disabled - skipping property sync');
      }
      return;
    }

    // Skip if already processing to prevent concurrent updates
    if (this.isProcessing) {
      if (this.debug) {
        console.log('Already processing cart properties - skipping');
      }
      return;
    }

    try {
      this.isProcessing = true;

      const [cart, dispatchData] = await Promise.all([
        this.fetchCart(),
        Promise.resolve(this.parseDispatchData())
      ]);

      if (!cart.items || cart.items.length === 0) {
        if (this.debug) console.log('Cart empty - no properties to sync');
        return;
      }

      // No dispatch data means we're not on a cart page/drawer - skip silently
      if (dispatchData.length === 0) {
        if (this.debug) {
          console.log('No dispatch data available - skipping property sync');
        }
        return;
      }

      // Build updates object for all items that need property changes
      const updates = {};
      let hasChanges = false;

      cart.items.forEach((item, index) => {
        // Find matching dispatch data
        const itemDispatchData = dispatchData.find(d => 
          d.variant_id === item.variant_id || d.product_id === item.product_id
        );

        if (!itemDispatchData) {
          return;
        }

        const delayInfo = this.calculateItemDispatchDelay(item, itemDispatchData);
        const currentDelayProperty = item.properties?._dispatch_delay || item.properties?.['_Dispatch Delay'];
        const currentReleaseDateProperty = item.properties?._release_date || item.properties?.['_Release Date'];

        // Determine what the property should be
        let targetDelayProperty = null;
        
        // If has release date property, don't set delay
        if (!currentReleaseDateProperty && delayInfo) {
          targetDelayProperty = delayInfo.message;
        }

        // Check if we need to update
        if (currentDelayProperty !== targetDelayProperty) {
          hasChanges = true;
          
          // Build property object for this line item
          const properties = { ...(item.properties || {}) };
          
          if (targetDelayProperty) {
            properties['_Dispatch Delay'] = targetDelayProperty;
          } else {
            // Remove property if it exists
            delete properties['_Dispatch Delay'];
            delete properties['_dispatch_delay'];
          }

          updates[item.key] = {
            quantity: item.quantity,
            properties: properties
          };

        }
      });

      // Apply updates if needed
      if (hasChanges) {
        await this.updateCartProperties(updates);
      }

    } catch (error) {
      console.error('Error syncing line item properties:', error);
    } finally {
      // Always reset processing flag
      this.isProcessing = false;
    }
  }

  /**
   * Update cart line item properties
   * Uses /cart/change.js for each item since /cart/update.js doesn't support properties
   * @param {object} updates - Object with line keys and their property updates
   */
  async updateCartProperties(updates) {
    try {
      const updateKeys = Object.keys(updates);
      
      if (updateKeys.length === 0) {
        return;
      }

      // Fetch fresh cart to validate line item keys are still valid
      const currentCart = await this.fetchCart();
      const validLineKeys = new Set(currentCart.items.map(item => item.key));

      // Filter out invalid line item keys
      const validUpdates = updateKeys.filter(key => {
        if (!validLineKeys.has(key)) {
          if (this.debug) {
            console.warn(`Skipping update for invalid line key: ${key}`);
          }
          return false;
        }
        return true;
      });

      if (validUpdates.length === 0) {
        if (this.debug) {
          console.log('No valid line items to update');
        }
        return;
      }

      // Use /cart/change.js for each line item (supports properties)
      const updatePromises = validUpdates.map(async (lineKey) => {
        const update = updates[lineKey];
        
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: lineKey,
            quantity: update.quantity,
            properties: update.properties
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.warn(`Failed to update ${lineKey}: ${response.statusText}`, errorText);
          return null; // Return null instead of throwing to allow other updates to continue
        }
        
        return await response.json();
      });

      // Wait for all updates to complete
      const results = await Promise.all(updatePromises);
      
      // Filter out failed updates
      const successfulResults = results.filter(r => r !== null);
      
      if (successfulResults.length > 0) {
        // Dispatch event to notify other components
        document.dispatchEvent(new CustomEvent('cart:properties-updated'));
      }
      
      return successfulResults;
    } catch (error) {
      console.error('Error updating cart properties:', error);
      // Don't rethrow - allow the script to continue
    }
  }


  /**
   * Main dispatch calculation function
   * Applies all business rules and returns dispatch estimate
   */
  async recalculateDispatch() {
    const dispatchData = this.parseDispatchData();
    
    if (dispatchData.length === 0) {
      this.clearCartAttribute();
      return;
    }

    // Sync line item properties first (non-blocking)
    this.syncLineItemProperties().catch(err => {
      console.error('Error syncing properties:', err);
    });

    // Check for release dates, but only for items where quantity exceeds stock
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for date comparison
    
    const releaseDates = dispatchData
      .filter(item => {
        // Only consider release dates if this item needs supplier stock
        const quantity = parseInt(item.quantity) || 0;
        const inStock = parseInt(item.in_stock_quantity) || 0;
        return item.release_date && quantity > inStock;
      })
      .map(item => this.parseReleaseDate(item.release_date))
      .filter(date => date !== null)
      .filter(date => date >= now); // Only include future or today's release dates
    
    if (releaseDates.length > 0) {
      // Use latest release date (only for items that need supplier stock)
      const latestReleaseDate = new Date(Math.max(...releaseDates));
      const estimate = `Estimated dispatch on or after ${this.formatDate(latestReleaseDate)}`;
      
      
      this.updateDispatchEstimate(estimate);
      return;
    }

    // No release dates - calculate based on delays
    let globalMinDelay = 0;
    let globalMaxDelay = 0;
    let hasSupplierDelays = false;
    let allInStock = true;

    dispatchData.forEach(item => {
      const quantity = parseInt(item.quantity) || 0;
      const inStock = parseInt(item.in_stock_quantity) || 0;
      const minDelay = parseInt(item.dispatch_delay_min) || 0;
      const maxDelay = parseInt(item.dispatch_delay_max) || 0;
      
      // Check if this item needs supplier stock
      const needsSupplierStock = quantity > inStock;
      
      if (needsSupplierStock) {
        allInStock = false;
        hasSupplierDelays = true;
        
        // Use supplier delays or theme defaults
        const itemMinDelay = minDelay || window.themeSettings?.dispatch_default_min_days || 1;
        const itemMaxDelay = maxDelay || window.themeSettings?.dispatch_default_max_days || 3;
        
        globalMinDelay = Math.max(globalMinDelay, itemMinDelay);
        globalMaxDelay = Math.max(globalMaxDelay, itemMaxDelay);
        
        if (this.debug) {
          console.log(`Item ${item.product_id}: needs supplier stock, using delays ${itemMinDelay}-${itemMaxDelay}`);
        }
      } else {
        // Item is in stock - will use default in-stock timing (0-1 days)
        if (this.debug) {
          console.log(`Item ${item.product_id}: in stock (${inStock} available), will use default in-stock timing`);
        }
      }
    });

    // If all items are in stock and no custom delays, use 0-1 day window
    if (allInStock && globalMinDelay === 0 && globalMaxDelay === 0) {
      globalMinDelay = window.themeSettings?.dispatch_in_stock_min_days || 0;
      globalMaxDelay = window.themeSettings?.dispatch_in_stock_max_days || 1;
      
      if (this.debug) {
        console.log('All items in stock, using default 0-1 day window');
      }
    }

    // Calculate dates using effective start date (accounting for cutoff time)
    const effectiveStartDate = this.getEffectiveStartDate();
    const minDate = this.addWorkingDays(effectiveStartDate, globalMinDelay);
    const maxDate = this.addWorkingDays(effectiveStartDate, globalMaxDelay);
    
    const minFormatted = this.formatDate(minDate);
    const maxFormatted = this.formatDate(maxDate);
    
    // Generate estimate string
    let estimate;
    if (minFormatted === maxFormatted) {
      estimate = `Estimated dispatch on ${minFormatted}`;
    } else {
      estimate = `Estimated dispatch between ${minFormatted} and ${maxFormatted}`;
    }
    
    if (this.debug) {
      console.log('Calculated dispatch estimate:', {
        effectiveStartDate: this.formatDate(effectiveStartDate),
        globalMinDelay,
        globalMaxDelay,
        minDate: minFormatted,
        maxDate: maxFormatted,
        estimate
      });
    }
    
    this.updateDispatchEstimate(estimate);
  }

  /**
   * Update cart attribute and UI with dispatch estimate
   * @param {string} estimate - Dispatch estimate string
   */
  updateDispatchEstimate(estimate) {
    // Update cart attribute
    this.updateCartAttribute(estimate);
    
    // Update UI element if it exists
    const messageElement = document.getElementById('dispatch-message');
    if (messageElement) {
      messageElement.textContent = estimate;
    }
    
    // Dispatch custom event for other components
    document.dispatchEvent(new CustomEvent('dispatch:updated', {
      detail: { estimate }
    }));
  }

  /**
   * Update cart attribute via Shopify API
   * @param {string} message - Dispatch estimate message
   */
  updateCartAttribute(message) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        attributes: { 
          dispatch_estimate: message 
        } 
      })
    })
    .then(response => response.json())
    .then(data => {
      if (this.debug) {
        console.log('Cart attribute updated:', message);
      }
    })
    .catch(error => {
      console.error('Error updating cart attribute:', error);
    });
  }

  /**
   * Clear cart dispatch attribute when cart is empty
   */
  clearCartAttribute() {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        attributes: { 
          dispatch_estimate: '' 
        } 
      })
    })
    .then(response => response.json())
    .then(data => {
      if (this.debug) {
        console.log('Cart dispatch attribute cleared (empty cart)');
      }
      
      // Clear UI element if it exists
      const messageElement = document.getElementById('dispatch-message');
      if (messageElement) {
        messageElement.textContent = '';
        messageElement.style.display = 'none';
      }
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('dispatch:cleared'));
    })
    .catch(error => {
      console.error('Error clearing cart attribute:', error);
    });
  }


  /**
   * Debounced recalculation to prevent excessive calls
   * @param {number} delay - Delay in milliseconds (default: 150ms)
   */
  debouncedRecalculate(delay = 150) {
    if (this.recalculateTimeout) {
      clearTimeout(this.recalculateTimeout);
    }
    
    this.recalculateTimeout = setTimeout(() => {
      this.recalculateDispatch();
    }, delay);
  }

  /**
   * Debounced property sync to batch updates
   * @param {number} delay - Delay in milliseconds (default: 300ms)
   */
  debouncedPropertySync(delay = 300) {
    if (this.syncPropertiesTimeout) {
      clearTimeout(this.syncPropertiesTimeout);
    }
    
    this.syncPropertiesTimeout = setTimeout(() => {
      this.syncLineItemProperties().catch(err => {
        console.error('Error in debounced property sync:', err);
      });
    }, delay);
  }

  /**
   * Setup event listeners for cart changes
   */
  setupEventListeners() {
    // Listen for cart updates (most common event)
    document.addEventListener('cart:updated', () => {
      this.debouncedRecalculate(150);
    });

    // Listen for cart item added
    document.addEventListener('cart:item-added', () => {
      // Immediate sync for new items, debounced recalculate
      this.debouncedPropertySync(100);
      this.debouncedRecalculate(200);
    });

    // Listen for cart emptied
    document.addEventListener('cart:emptied', () => {
      this.clearCartAttribute();
    });

    // Listen for Shopify section reloads (cart page)
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail && event.detail.sectionId && event.detail.sectionId.includes('cart')) {
        this.debouncedRecalculate(100);
      }
    });

    // Fallback: Monitor DOM changes for new dispatch data
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        const hasDispatchData = mutations.some(mutation => 
          Array.from(mutation.addedNodes).some(node => 
            node.nodeType === 1 && (
              node.classList?.contains('dispatch-data') ||
              node.querySelector?.('.dispatch-data')
            )
          )
        );
        
        if (hasDispatchData) {
          this.debouncedRecalculate(150);
        }
      });
      
      observer.observe(document.body, { childList: true, subtree: true });
    }

    if (this.debug) {
      console.log('Event listeners setup complete');
    }
  }


  /**
   * Manual refresh method for external use
   */
  refresh() {
    this.recalculateDispatch();
  }

  /**
   * Manual property sync for testing/debugging
   * @returns {Promise<void>}
   */
  async manualSync() {
    console.log('Manual sync triggered');
    await this.syncLineItemProperties();
  }
}

// Initialize global instance
window.cartDispatchCalculator = new CartDispatchCalculator();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartDispatchCalculator;
}
